import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import ExportButton from "./ExportButton";
import styles from "./ChartModal.module.css";
import layoutStyles from "../../styles/utilities/layout.module.css";

export type ExportFormat = "csv" | "pdf" | string;

interface ChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  onExport?: (format: ExportFormat) => void;
  headerRight?: ReactNode | null;
}

const ChartModal = ({
  isOpen,
  onClose,
  title,
  subtitle = "",
  children,
  onExport,
  headerRight = null,
}: ChartModalProps) => {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const prevFocusedRef = useRef<Element | null>(null);
  const wasKeyboardRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = () => {
      wasKeyboardRef.current = true;
    };
    const handleMouseDown = () => {
      wasKeyboardRef.current = false;
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    prevFocusedRef.current = document.activeElement;

    const container = frameRef.current;
    if (!container) return;

    const getFocusable = () => {
      const selectors = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ];
      return Array.from(container.querySelectorAll<HTMLElement>(selectors.join(","))).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1 && el.offsetParent !== null
      );
    };

    const focusables = getFocusable();

    if (wasKeyboardRef.current) {
      (focusables[0] || container).focus();
    } else {
      container.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const items = getFocusable();
      if (!items.length) {
        e.preventDefault();
        container.focus();
        return;
      }
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey) {
        if (currentIndex <= 0) {
          e.preventDefault();
          items[items.length - 1].focus();
        }
      } else {
        if (currentIndex === items.length - 1) {
          e.preventDefault();
          items[0].focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      const prev = prevFocusedRef.current as HTMLElement | null;
      prev?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const stop: React.MouseEventHandler<HTMLDivElement> = (e) => e.stopPropagation();

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modalFrame}
        onClick={stop}
        ref={frameRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chart-modal-title"
        tabIndex={-1}
      >
        <div className={styles.header}>
          <div
            className={`${layoutStyles.flexRow} ${layoutStyles.itemsStart} ${layoutStyles.justifyBetween}`}
          >
            <div className={layoutStyles.flexColumn}>
              <h2
                id="chart-modal-title"
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--color-text-primary)",
                  lineHeight: "var(--line-height-tight)",
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-muted)",
                  marginTop: "0.25rem",
                }}
              >
                {subtitle || "\u00A0"}
              </p>
            </div>
            <div
              className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${styles.exportContainer}`}
            >
              {onExport && (
                <div
                  className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.gap2}`}
                >
                  <span className={styles.downloadLabel}>Download</span>
                  <ExportButton
                    onClick={() => onExport("csv")}
                    label="CSV"
                    iconOnly={false}
                    showIcon={true}
                    className="!h-10 !px-4 !rounded-full !bg-white !border !border-gray-300 !text-gray-800 hover:!bg-gray-50"
                    title="Download as CSV"
                  />
                  <ExportButton
                    onClick={() => onExport("pdf")}
                    label="PDF"
                    iconOnly={false}
                    showIcon={true}
                    className="!h-10 !px-4 !rounded-full !bg-white !border !border-gray-300 !text-gray-800 hover:!bg-gray-50"
                    title="Download as PDF"
                  />
                </div>
              )}
              {headerRight}
              <button onClick={onClose} className={styles.closeBtn} aria-label="Close modal">
                <span className={styles.downloadLabel}>Close</span>
                <X style={{ height: "1.25rem", width: "1.25rem" }} />
              </button>
            </div>
          </div>
          <div className={styles.headerSeparator} />
        </div>
        <div className={styles.contentScroll}>{children}</div>
      </div>
    </div>
  );
};

export default ChartModal;
